import { useMemo } from 'react';

import { IHomePageConfigurationFragment } from 'generated/sanity-graphql';

import {
  FeatureHomePageComponentTypes,
  FeatureHomePageComponents,
  IRawFeatureHomePageComponent,
} from '../types';

import { useAlert } from './use-alert';
import { useAppDownload } from './use-app-download';
import { useBrazeContentCards } from './use-braze-content-cards';
import { useFanFavorites } from './use-fan-favorites';
import { useMainHero } from './use-main-hero';
import { useMarketingTileGroups } from './use-marketing-tile-groups';
import { useOurServices } from './use-our-services';
import { useRewardsSection } from './use-rewards-section';
import { useTrending } from './use-trending';

const ID_ONLY_COMPONENTS = [
  FeatureHomePageComponentTypes.LockedOffers,
  FeatureHomePageComponentTypes.OffersSection,
  FeatureHomePageComponentTypes.RecentItems,
  FeatureHomePageComponentTypes.RollUpTheRimSection,
  FeatureHomePageComponentTypes.ScanForRewards,
];

/**
 * @name formatIdOnlyComponents
 * @description
 *  At this point there are some components we only care about their _id
 *    and we don't need to query for them because we already have the _id
 *    from the original homepage query.
 *  So, lets just format them to mirror the queried components
 */
const formatIdOnlyComponents = (featureHomePageComponentMap: any) => {
  return ID_ONLY_COMPONENTS.reduce((acc, compType) => {
    if (featureHomePageComponentMap[compType]) {
      acc[compType] = {
        _id: featureHomePageComponentMap[compType],
        __typename: compType,
      };
    }
    return acc;
  }, {});
};

const orderPopulatedFeatureHomePageComponents = (
  allPopulatedComponents: FeatureHomePageComponents,
  featureHomePage?: IHomePageConfigurationFragment
) => {
  const components = (featureHomePage?.components ?? []).reduce(
    (acc: FeatureHomePageComponents[], rawComp: IRawFeatureHomePageComponent) => {
      if (rawComp?.__typename && allPopulatedComponents[rawComp?.__typename]) {
        switch (rawComp.__typename) {
          // MarketingTileGroup type is allowed multiple instances
          case FeatureHomePageComponentTypes.MarketingTileGroup:
            const specificMarketingTileGroup = allPopulatedComponents[rawComp.__typename].find(
              (popComp: any) => popComp._id === rawComp._id
            );
            if (specificMarketingTileGroup) {
              acc.push(specificMarketingTileGroup);
            }
            break;
          // all others are limited to one instance
          default:
            acc.push(allPopulatedComponents[rawComp?.__typename]);
        }
      }
      return acc;
    },
    [] as FeatureHomePageComponents[]
  );
  return components;
};

export const useFeatureHomePageComponents = (featureHomePage?: IHomePageConfigurationFragment) => {
  /**
   * @name featureHomePageComponentMap
   * @description
   *  maps raw feature home page components to { type: id } structure
   *  to simplify querying for the different component types
   */
  const featureHomePageComponentMap: FeatureHomePageComponents = (
    featureHomePage?.components ?? []
  ).reduce((acc: any, comp: IRawFeatureHomePageComponent) => {
    switch (comp.__typename) {
      // MarketingTileGroup type is allowed multiple instances
      case FeatureHomePageComponentTypes.MarketingTileGroup:
        if (!acc[comp.__typename]) {
          acc[comp.__typename] = [];
        }
        acc[comp.__typename].push(comp._id);
        break;
      // all others are limited to one instance
      default:
        acc[comp.__typename] = comp._id;
    }
    return acc;
  }, {});

  /**
   * ==================================
   *    INDIVIDUAL COMPONENT QUERIES
   * ==================================
   */
  const { mainHero, mainHeroLoading } = useMainHero(featureHomePageComponentMap.MainHero);

  const { brazeContentCards, brazeContentCardsLoading } = useBrazeContentCards(
    featureHomePageComponentMap.BrazeContentCards
  );

  const { alert, alertLoading } = useAlert(featureHomePageComponentMap.Alert);

  const { marketingTileGroups, marketingTileGroupsLoading } = useMarketingTileGroups(
    featureHomePageComponentMap.MarketingTileGroup
  );

  const { trending, trendingLoading } = useTrending(featureHomePageComponentMap.Trending);

  const { fanFavorites, fanFavoritesLoading } = useFanFavorites(
    featureHomePageComponentMap.FanFavorites
  );

  const { ourServices, ourServicesLoading } = useOurServices(
    featureHomePageComponentMap.OurServices
  );

  const { appDownload, appDownloadLoading } = useAppDownload(
    featureHomePageComponentMap.AppDownload
  );

  const { rewardsSection, rewardsSectionLoading } = useRewardsSection(
    featureHomePageComponentMap.RewardsSection
  );

  /**
   * ==================================
   *   MERGE POPULATED COMPONENT DATA
   * ==================================
   */
  const allPopulatedComponents: FeatureHomePageComponents = {
    [FeatureHomePageComponentTypes.Alert]: alert,
    [FeatureHomePageComponentTypes.AppDownload]: appDownload,
    [FeatureHomePageComponentTypes.BrazeContentCards]: brazeContentCards,
    [FeatureHomePageComponentTypes.FanFavorites]: fanFavorites,
    [FeatureHomePageComponentTypes.MainHero]: mainHero,
    [FeatureHomePageComponentTypes.MarketingTileGroup]: marketingTileGroups,
    [FeatureHomePageComponentTypes.OurServices]: ourServices,
    [FeatureHomePageComponentTypes.RewardsSection]: rewardsSection,
    [FeatureHomePageComponentTypes.Trending]: trending,
    ...formatIdOnlyComponents(featureHomePageComponentMap),
  };

  /**
   * ==================================
   *   ORDER POPULATED COMPONENT DATA
   * ==================================
   */
  const orderedPopulatedFeatureHomePageComponents = useMemo(
    () => orderPopulatedFeatureHomePageComponents(allPopulatedComponents, featureHomePage),
    [allPopulatedComponents, featureHomePage]
  );

  /**
   * ==================================
   *    DETERMINE LOADING STATE
   * ==================================
   */
  const featureHomePageComponentsLoading =
    mainHeroLoading ||
    brazeContentCardsLoading ||
    alertLoading ||
    marketingTileGroupsLoading ||
    trendingLoading ||
    fanFavoritesLoading ||
    ourServicesLoading ||
    appDownloadLoading ||
    rewardsSectionLoading;

  /**
   * ==================================
   *            RETURN DATA
   * ==================================
   */
  return {
    featureHomePageComponentsLoading,
    featureHomePageComponents: orderedPopulatedFeatureHomePageComponents,
  };
};
